/**
 * MaintenanceWaitlistCard — Phase F.1
 *
 * Public, embedded card on /maintenance-plans (above the fold).
 * Lets homeowners hold their spot for the July 2026 launch while
 * Stripe LIVE keys are pending.
 *
 * Flow:
 *   1. Visitor fills name + email + phone + address + tier + sqft.
 *   2. We compute the "Hold your spot — $X due at launch" badge from
 *      the chosen sqft tier (sqftTier inspection fee) and the chosen
 *      tier's monthly price for that sqft tier.
 *   3. Submit → POST /api/public/waitlist → success state with
 *      receipt-style summary of held spot + launch window.
 *
 * No charge today. The badge is informational only — Stripe LIVE
 * keys arrive separately for actual checkout later.
 */
import { useMemo, useState } from "react";
import {
  CalendarClock,
  CheckCircle2,
  Hourglass,
  Loader2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import {
  RESIDENTIAL_PLANS,
  COMMERCIAL_PLANS,
  SQFT_TIERS,
  findTierForSqft,
} from "@/data/maintenancePlans";

const ALL_PLANS = [...RESIDENTIAL_PLANS, ...COMMERCIAL_PLANS];

const PROPERTY_TYPE_OPTIONS = [
  { value: "residential", label: "Residential (my home)" },
  { value: "commercial", label: "Commercial (office / retail / portfolio)" },
];

function getApiBase() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
}

function formatUsd(cents) {
  if (!cents || cents <= 0) return null;
  return `$${(cents / 100).toFixed(0)}`;
}

function digitsOnly(s) {
  return (s || "").replace(/[^\d]/g, "");
}

// Resolve the chosen plan + tier into the actual numbers shown on the
// "Hold your spot" badge.
function resolvePricing({ tierSlug, sqft }) {
  if (!tierSlug || tierSlug === "undecided") return null;
  const plan = ALL_PLANS.find((p) => p.id === tierSlug);
  if (!plan) return null;
  // Commercial plans don't run through sqft tiers — use the plan's flat price.
  const isResidential = RESIDENTIAL_PLANS.some((p) => p.id === tierSlug);
  if (!isResidential) {
    return {
      planName: plan.name,
      inspectionFeeLabel: "Custom quote required",
      inspectionFeeCents: null,
      monthlyPriceLabel: `Starting at $${plan.price}/${plan.billing}`,
      monthlyPriceCents: plan.price ? plan.price * 100 : null,
      sqftTierId: null,
    };
  }
  const sqftTier = findTierForSqft(sqft) || SQFT_TIERS[1]; // default to Tier 2 (Austin median)
  const cell = sqftTier.plans?.[tierSlug] || {};
  return {
    planName: plan.name,
    inspectionFeeLabel:
      typeof sqftTier.inspectionFee === "number"
        ? `$${sqftTier.inspectionFee}`
        : sqftTier.inspectionFeeLabel || "Custom quote required",
    inspectionFeeCents:
      typeof sqftTier.inspectionFee === "number"
        ? sqftTier.inspectionFee * 100
        : null,
    monthlyPriceLabel: cell.priceLabel || `Starting at $${plan.price}/${plan.billing}`,
    monthlyPriceCents: typeof cell.price === "number" ? cell.price * 100 : null,
    sqftTierId: sqftTier.id,
  };
}

export default function MaintenanceWaitlistCard() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "Austin",
    state: "TX",
    zip_code: "",
    property_type: "residential",
    preferred_tier: "homecare-plus",
    sqft: "",
    interest_note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const sqftN = digitsOnly(form.sqft) ? parseInt(digitsOnly(form.sqft), 10) : 0;
  const pricing = useMemo(
    () => resolvePricing({ tierSlug: form.preferred_tier, sqft: sqftN }),
    [form.preferred_tier, sqftN],
  );

  const planOptions = useMemo(() => {
    const list = form.property_type === "commercial" ? COMMERCIAL_PLANS : RESIDENTIAL_PLANS;
    return [
      ...list.map((p) => ({ value: p.id, label: p.name })),
      { value: "undecided", label: "Not sure yet — help me choose" },
    ];
  }, [form.property_type]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function onPropertyTypeChange(v) {
    setForm((f) => ({
      ...f,
      property_type: v,
      preferred_tier:
        v === "commercial" ? "propertycare-core" : "homecare-plus",
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setError("");
    const trimmedName = (form.name || "").trim();
    const trimmedEmail = (form.email || "").trim();
    if (trimmedName.length < 2 || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) {
      setError("Please add your name and a valid email so we can confirm your spot.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: trimmedName,
        email: trimmedEmail,
        phone: digitsOnly(form.phone) || "",
        address: form.address || "",
        city: form.city || "",
        state: form.state || "TX",
        zip_code: form.zip_code || "",
        preferred_tier: form.preferred_tier,
        property_type: form.property_type,
        sqft: sqftN || null,
        sqft_tier_id: pricing?.sqftTierId || null,
        inspection_fee_cents: pricing?.inspectionFeeCents || null,
        monthly_price_cents: pricing?.monthlyPriceCents || null,
        interest_note: (form.interest_note || "").trim(),
        source_page: "/maintenance-plans",
      };
      const apiBase = getApiBase();
      const res = await fetch(`${apiBase}/api/public/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      setSuccess({ ...body, pricing, name: trimmedName });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <section
        id="waitlist"
        className="bg-gradient-to-b from-br-paper to-white border border-br-bronze-2/30 rounded-2xl shadow-lg overflow-hidden"
        data-testid="maintenance-waitlist-success"
      >
        <div className="p-7 sm:p-10">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-emerald-100 text-emerald-700 p-2 shrink-0">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div>
              <div className="br-eyebrow text-emerald-700">You&rsquo;re on the list</div>
              <h2 className="br-headline text-2xl sm:text-3xl mt-1.5 text-br-black">
                Thanks {success.name?.split(" ")[0] || "—"}, your spot is held for our {success.launch_window || "July"} launch.
              </h2>
              <p className="mt-3 text-gray-700 leading-relaxed">
                We just emailed you a confirmation. You&rsquo;ll get one short note in 3 days and a launch reminder 10 days before activation. No charge today, no surprise charges — we won&rsquo;t lock anything in without your go.
              </p>
            </div>
          </div>

          {success.pricing ? (
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-br-bronze-2/40 bg-white p-4">
                <div className="br-eyebrow text-br-bronze-2 text-[10px]">Held tier</div>
                <div className="text-br-black font-semibold mt-1">{success.pricing.planName}</div>
              </div>
              <div className="rounded-lg border border-br-bronze-2/40 bg-white p-4">
                <div className="br-eyebrow text-br-bronze-2 text-[10px]">Initial inspection fee</div>
                <div className="text-br-black font-semibold mt-1">{success.pricing.inspectionFeeLabel}</div>
                <div className="text-[11.5px] text-gray-500 mt-1">
                  Due only if you activate after launch. No charge today.
                </div>
              </div>
              <div className="rounded-lg border border-br-bronze-2/40 bg-white p-4">
                <div className="br-eyebrow text-br-bronze-2 text-[10px]">Target monthly</div>
                <div className="text-br-black font-semibold mt-1">{success.pricing.monthlyPriceLabel}</div>
              </div>
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap gap-3 items-center text-sm text-gray-600">
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              <ShieldCheck className="h-4 w-4" /> Spot held — no charge today
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="h-4 w-4 text-br-bronze-2" />
              Launch: {success.launch_window || "July 2026"}
            </span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="waitlist"
      className="relative bg-gradient-to-br from-br-black via-[#1c1410] to-br-black text-white rounded-2xl shadow-2xl overflow-hidden border border-br-bronze-2/30"
      data-testid="maintenance-waitlist-card"
    >
      {/* subtle texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
      <div className="relative grid lg:grid-cols-5 gap-8 p-7 sm:p-10">
        {/* Left — pitch */}
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-br-bronze-2/40 bg-br-bronze-2/10 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-br-bronze-2 font-semibold">
            <Sparkles className="h-3.5 w-3.5" />
            July 2026 Launch
          </div>
          <h2 className="br-headline text-3xl sm:text-4xl mt-4 leading-tight">
            Hold your spot for HomeCare before we open.
          </h2>
          <p className="mt-4 text-gray-300 leading-relaxed">
            Black Ridge HomeCare opens for a limited number of inaugural members in <strong>July 2026</strong>. Join the waitlist today &mdash; no card needed &mdash; and we&rsquo;ll lock in your tier, your inspection fee, and your target monthly price before pricing tightens.
          </p>
          <ul className="mt-5 space-y-2.5 text-[14px] text-gray-300">
            <li className="flex items-start gap-2.5">
              <Hourglass className="h-4 w-4 text-br-bronze-2 mt-0.5 shrink-0" />
              <span><strong className="text-white">Spot held immediately.</strong> No charge today, no surprise charges later.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <ShieldCheck className="h-4 w-4 text-br-bronze-2 mt-0.5 shrink-0" />
              <span><strong className="text-white">Inspection fee locked in</strong> at the tier we quote you now. Only applies if you choose to activate after launch.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <CalendarClock className="h-4 w-4 text-br-bronze-2 mt-0.5 shrink-0" />
              <span><strong className="text-white">Activation link at launch.</strong> One click to confirm + book your baseline inspection. Membership monthly billing only starts after the baseline.</span>
            </li>
          </ul>

          {/* Live "hold your spot" badge */}
          {pricing ? (
            <div
              className="mt-6 rounded-xl border border-br-bronze-2/50 bg-br-bronze-2/10 p-4"
              data-testid="hold-your-spot-badge"
            >
              <div className="text-[11px] uppercase tracking-[0.16em] text-br-bronze-2 font-semibold">
                Hold your spot
              </div>
              <div className="mt-1.5 text-white text-lg font-semibold leading-snug">
                {pricing.inspectionFeeCents
                  ? `${formatUsd(pricing.inspectionFeeCents)} initial inspection at launch`
                  : "Custom quote at launch"}
              </div>
              <div className="text-[12.5px] text-gray-300 mt-1">
                {pricing.planName} &middot; target {pricing.monthlyPriceLabel}
              </div>
              <div className="text-[11px] text-amber-200/80 mt-2">
                No charge today &mdash; you only pay the inspection fee at launch if you confirm.
              </div>
            </div>
          ) : null}
        </div>

        {/* Right — form */}
        <form onSubmit={submit} className="lg:col-span-3 space-y-3" data-testid="maintenance-waitlist-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Full name" required>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Alex Johnson"
                className="wl-input"
                required
                data-testid="waitlist-name"
              />
            </Field>
            <Field label="Email" required>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="alex@example.com"
                className="wl-input"
                required
                data-testid="waitlist-email"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Phone (optional)">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="(512) 555-0100"
                className="wl-input"
                data-testid="waitlist-phone"
              />
            </Field>
            <Field label="Property zip" hint="Helps us confirm service area">
              <input
                type="text"
                value={form.zip_code}
                onChange={(e) => update("zip_code", e.target.value.slice(0, 6))}
                placeholder="78704"
                className="wl-input"
                data-testid="waitlist-zip"
              />
            </Field>
          </div>

          <Field label="Property address (optional)" hint="Street address only — used for service area check">
            <input
              type="text"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
              placeholder="123 Cedar Street"
              className="wl-input"
              data-testid="waitlist-address"
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Property type">
              <select
                value={form.property_type}
                onChange={(e) => onPropertyTypeChange(e.target.value)}
                className="wl-input"
                data-testid="waitlist-property-type"
              >
                {PROPERTY_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Estimated sq ft" hint="Drives your initial inspection fee">
              <input
                type="text"
                inputMode="numeric"
                value={form.sqft}
                onChange={(e) => update("sqft", e.target.value.slice(0, 6))}
                placeholder="2,200"
                className="wl-input"
                data-testid="waitlist-sqft"
              />
            </Field>
          </div>

          <Field label="Preferred tier" hint={`We'll hold this exact plan for the ${success?.launch_window || "July 2026"} launch`}>
            <select
              value={form.preferred_tier}
              onChange={(e) => update("preferred_tier", e.target.value)}
              className="wl-input"
              data-testid="waitlist-tier"
            >
              {planOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          <Field label="What got you interested? (optional)">
            <textarea
              value={form.interest_note}
              onChange={(e) => update("interest_note", e.target.value.slice(0, 1000))}
              placeholder="Storm prep, recurring maintenance, older home, new buyer, etc."
              rows={3}
              className="wl-input resize-none"
              data-testid="waitlist-note"
            />
          </Field>

          {error ? (
            <div className="rounded-md border border-red-400/50 bg-red-500/10 px-3 py-2 text-[13px] text-red-200">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="br-btn-primary w-full justify-center mt-1"
            data-testid="waitlist-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Holding your spot…
              </>
            ) : (
              "Hold my spot for July"
            )}
          </button>
          <p className="text-[11px] text-gray-400 leading-snug text-center mt-1">
            No charge today. You can change your tier or remove yourself any time before launch by replying to the confirmation email.
          </p>
        </form>
      </div>

      <style>{`
        .wl-input {
          width: 100%;
          background: rgba(0,0,0,0.4);
          border: 1px solid rgba(163, 123, 37, 0.35);
          color: #fff;
          padding: 10px 12px;
          border-radius: 6px;
          font-size: 14px;
          line-height: 1.4;
          transition: border-color .15s, box-shadow .15s, background .15s;
        }
        .wl-input::placeholder { color: rgba(255,255,255,0.4); }
        .wl-input:focus {
          outline: none;
          border-color: rgba(163, 123, 37, 0.85);
          box-shadow: 0 0 0 3px rgba(163, 123, 37, 0.18);
          background: rgba(0,0,0,0.55);
        }
        .wl-input:disabled { opacity: .55; cursor: not-allowed; }
        select.wl-input option { background: #1a1410; color: #fff; }
      `}</style>
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.14em] text-br-bronze-2 font-semibold">
        {label}
        {required ? <span className="text-red-300 ml-0.5">*</span> : null}
      </span>
      <div className="mt-1.5">{children}</div>
      {hint ? <div className="text-[10.5px] text-gray-400 mt-1">{hint}</div> : null}
    </label>
  );
}
